import React from "react";
import { AgGrid, Button } from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { actionButton } from "#common/components/ActionButton/styles_vanilla.css";
import { PaymentServicePaymentBatchStatus } from "#graphql/supergraphEnums";
import {
  DEF_FILTER_DEBOUNCE_MS,
  DELETED_DISTRIBUTION_LABEL,
  DELETED_TRANSACTION_LABEL,
} from "../../../constants";
import { ClientFilter } from "../../../GridFilters/ClientFilter";
import { EntityFilter } from "../../../GridFilters/EntityFilter";
import Money from "../../../Money";
import { CombinedStatusRenderer } from "../../../shared/CombinedStatusRenderer";
import { TextWithIcon } from "../../../TextWithIcon";
import { BatchStatusToolTip } from "../BatchStatusToolTip";
import {
  verticalCenterWrapper,
  deletedBatchText,
  actionButtonTextStyle,
} from "../styles_vanilla.css";
import { PaymentRowItem } from "../types";
import { batchStatusFilterTypes } from "../util/batchStatusUtil";
import { paymentStatusFilterTypes } from "../util/paymentStatusUtils";

/**
 * List of the supported columns in the Payment Batches table
 */
export enum SupportedColumns {
  ClientName = "clientName",
  EntityName = "entityName",
  PaymentType = "paymentType",
  Payee = "payee",
  PaymentDate = "paymentDate",
  ActionForGpx = "actionForGpx",
  ActionForCanopy = "actionForCanopy",
  Status = "status",
  BatchStatus = "batchStatus",
  PaymentStatus = "paymentStatus",
  PaymentAmount = "paymentAmount",
}

/**
 * The mapper provides the corresponding column name displayed in the table
 */
export const columnNames = new Map<SupportedColumns, string>([
  [SupportedColumns.Payee, "Payee"],
  [SupportedColumns.Status, "Status"],
  [SupportedColumns.BatchStatus, "Batch Status"],
  [SupportedColumns.PaymentStatus, "Payment Status"],
  [SupportedColumns.PaymentType, "Payment type"],
  [SupportedColumns.ClientName, "Client"],
  [SupportedColumns.EntityName, "Entity"],
  [SupportedColumns.PaymentAmount, "Payment amount"],
  [SupportedColumns.PaymentDate, "Payment date"],

  [SupportedColumns.ActionForCanopy, "Action"],
  [SupportedColumns.ActionForGpx, "Action"],
]);

export const columnsForCanopy = [
  SupportedColumns.ClientName,
  SupportedColumns.EntityName,
  SupportedColumns.PaymentType,
  SupportedColumns.Payee,
  SupportedColumns.Status,
  SupportedColumns.PaymentAmount,
  SupportedColumns.PaymentDate,
  SupportedColumns.ActionForCanopy,
];

export const columnsForGPX = [
  SupportedColumns.EntityName,
  SupportedColumns.PaymentType,
  SupportedColumns.Payee,
  SupportedColumns.Status,
  SupportedColumns.PaymentAmount,
  SupportedColumns.PaymentDate,
  SupportedColumns.ActionForGpx,
];

/**
 * AG Grid, column definitions for the Payment Batches table
 * @param isGPX - Flag to determine if GPX-specific configurations should be applied
 * @param isM3Enabled - Flag to determine if M3 capital activity support is enabled
 * @returns Array of column definitions
 */
export const getColumnDefinitions: (
  isGPX: boolean,
  isM3Enabled?: boolean,
) => AgGrid.ColDef[] = (isGPX, isM3Enabled) => {
  const columnDefinitions: AgGrid.ColDef[] = [
    {
      field: SupportedColumns.ClientName,
      headerName: columnNames.get(SupportedColumns.ClientName),
      sortable: false,
      filter: ClientFilter,
      // Exclude filtering from the column header because UX isn't great
      menuTabs: ["generalMenuTab", "columnsMenuTab"],
    },
    {
      field: SupportedColumns.EntityName,
      headerName: columnNames.get(SupportedColumns.EntityName),
      sortable: false,
      filter: EntityFilter,
      // In GPX the Client column is hidden (the GP user is always
      // their own arena), so omit the dependentFilter. EntityFilter then
      // queries the arena-less `investmentEntities` field, which the
      // backend auto-scopes to the caller's arena for non-admin users
      // (per the EntitiesFilters.arenaIds schema comment).
      filterParams: isGPX ? {} : { dependentFilter: SupportedColumns.ClientName },
      // Exclude filtering from the column header because UX isn't great
      menuTabs: ["generalMenuTab", "columnsMenuTab"],
    },
    {
      field: SupportedColumns.PaymentType,
      headerName: columnNames.get(SupportedColumns.PaymentType),
      sortable: false,
      cellStyle: { display: "flex", alignItems: "center", overflow: "hidden" },
      cellRenderer: ({ data }: { data?: PaymentRowItem }) => {
        if (!data?.paymentType) return null;
        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ lineHeight: "18px" }}>{data.paymentType}</span>
            {data.paymentSubtitleLabel && (
              <span
                style={{ fontSize: "12px", color: Colors.Neutral600, lineHeight: "14px" }}
              >{`(${data.paymentSubtitleLabel})`}</span>
            )}
          </div>
        );
      },
    },
    {
      field: SupportedColumns.Payee,
      headerName: columnNames.get(SupportedColumns.Payee),
      sortable: false,
      cellRenderer: TextWithIcon,
      cellClass: verticalCenterWrapper,
      cellRendererParams: ({ data }: { data: PaymentRowItem }) => {
        // Legacy (non-M3): an empty payee means the underlying distribution was
        // deleted — show the deleted treatment with a warning icon.
        if (!isM3Enabled) {
          const exists = data.payee?.length || false;
          return {
            label: exists ? data.payee : DELETED_DISTRIBUTION_LABEL,
            className: exists ? undefined : deletedBatchText,
            iconType: exists ? undefined : "exclamation-triangle",
            iconColor: exists ? undefined : Colors.Danger600,
          };
        }
        // M3: the backend sends DELETED_DISTRIBUTION_LABEL as the description
        // only when the batch is genuinely deleted — that alone keeps the
        // warning treatment. A non-deleted batch with no resolvable payee
        // (e.g. a single-position batch with no investor account on file, or a
        // missing reference object) falls back to the entity name with no
        // alarming icon.
        if (data.payee === DELETED_DISTRIBUTION_LABEL) {
          return {
            label: DELETED_TRANSACTION_LABEL,
            className: deletedBatchText,
            iconType: "exclamation-triangle",
            iconColor: Colors.Danger600,
          };
        }
        return {
          label: data.payee?.length ? data.payee : data.entityName,
        };
      },
    },
    {
      field: SupportedColumns.Status,
      headerName: columnNames.get(SupportedColumns.Status),
      sortable: true,
      tooltipComponent: BatchStatusToolTip,
      filter: false,
      cellClass: verticalCenterWrapper,
      cellRenderer: ({ data }: { data: PaymentRowItem }) => {
        if (!data?.combinedStatusConfig) return null;
        return <CombinedStatusRenderer config={data.combinedStatusConfig} />;
      },
      valueFormatter: ({ data }: { data: PaymentRowItem }) => {
        return data?.combinedStatusConfig?.label;
      },
    },
    {
      field: SupportedColumns.BatchStatus,
      headerName: columnNames.get(SupportedColumns.BatchStatus),
      hide: true,
      filter: "agSetColumnFilter",
      filterParams: {
        defaultToNothingSelected: true,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
        showTooltips: true,
        values: [...batchStatusFilterTypes.keys()],
      },
      valueGetter: ({ data }: { data: PaymentRowItem }) => {
        return data?.combinedStatusConfig?.label;
      },
    },
    {
      field: SupportedColumns.PaymentStatus,
      headerName: columnNames.get(SupportedColumns.PaymentStatus),
      hide: true,
      filter: "agSetColumnFilter",
      filterParams: {
        defaultToNothingSelected: true,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
        showTooltips: true,
        values: [...paymentStatusFilterTypes.keys()],
      },
      valueGetter: ({ data }: { data: PaymentRowItem }) => {
        return data?.combinedStatusConfig?.label;
      },
    },
    {
      field: SupportedColumns.PaymentAmount,
      headerName: columnNames.get(SupportedColumns.PaymentAmount),
      sortable: true,
      filter: "agNumberColumnFilter",
      filterParams: {
        maxNumConditions: 1,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
        filterOptions: ["inRange", "greaterThan", "lessThan"],
      },
      cellRenderer: Money,
      cellRendererParams: ({ data }: { data: PaymentRowItem }) => ({
        money: data.paymentAmount,
        rounded: false,
      }),
      cellStyle: { textAlign: "right" },
    },
    {
      field: SupportedColumns.PaymentDate,
      headerName: columnNames.get(SupportedColumns.PaymentDate),
      sortable: false,
      resizable: false,
      filter: "agDateColumnFilter",
      filterParams: {
        buttons: ["reset"],
        maxNumConditions: 1,
        debounceMs: DEF_FILTER_DEBOUNCE_MS,
        filterOptions: ["inRange"],
      },
    },
    {
      field: SupportedColumns.ActionForGpx,
      headerName: columnNames.get(SupportedColumns.ActionForGpx),
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      flex: 0,
      width: 108,
      cellRenderer: ({ buttonText }: { buttonText: string }) => {
        return buttonText ? (
          <Button className={actionButtonTextStyle} variant="neutral">
            {buttonText}
          </Button>
        ) : null;
      },
      cellRendererParams: ({ data }: { data: PaymentRowItem }) => ({
        buttonText:
          PaymentServicePaymentBatchStatus.ReadyForReview === data.status
            ? "Review"
            : undefined,
      }),
    },
    {
      field: SupportedColumns.ActionForCanopy,
      headerName: columnNames.get(SupportedColumns.ActionForCanopy),
      sortable: false,
      resizable: false,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      flex: 0,
      width: 108,
      cellRenderer: ({ buttonText }: { buttonText: string }) => {
        return buttonText ? (
          <Button className={actionButton} variant="neutral">
            {buttonText}
          </Button>
        ) : null;
      },
      cellRendererParams: ({ data }: { data: PaymentRowItem }) => ({
        buttonText:
          PaymentServicePaymentBatchStatus.ReadyForTreasuryReview === data.status
            ? "Review"
            : undefined,
      }),
    },
  ];

  const columnsForDisplay = isGPX ? columnsForGPX : columnsForCanopy;

  // Hidden filter columns that should always be included
  const hiddenFilterColumns = [SupportedColumns.BatchStatus, SupportedColumns.PaymentStatus];

  const filteredColumnDefinitions = columnDefinitions.filter(
    (col) =>
      columnsForDisplay.includes(col.field as SupportedColumns) ||
      hiddenFilterColumns.includes(col.field as SupportedColumns),
  );

  // Find the first visible column (not hidden)
  const firstVisibleColumnIndex = filteredColumnDefinitions.findIndex((col) => !col.hide);
  // Find the last visible column (not hidden)
  const lastVisibleColumnIndex =
    filteredColumnDefinitions.length -
    1 -
    [...filteredColumnDefinitions].reverse().findIndex((col) => !col.hide);

  // Add the border classes to the first visible column
  if (firstVisibleColumnIndex >= 0) {
    filteredColumnDefinitions[firstVisibleColumnIndex].pinned = "left";
  }

  // Add the border classes to the last visible column and center
  if (lastVisibleColumnIndex >= 0) {
    filteredColumnDefinitions[lastVisibleColumnIndex].pinned = "right";
    filteredColumnDefinitions[lastVisibleColumnIndex].cellClass = verticalCenterWrapper;
  }

  return filteredColumnDefinitions;
};
